/**
 * Totaled Holdings Report.
 * @module advisor/components/reports/TotaledHoldingsReport
 *
 */

import React, {Component} from 'react';
import axiosInstance from '../../../shared/utils/axios';
import moment from 'moment';
import PropTypes from 'prop-types';
import Promise from 'bluebird';
import debug from 'debug';
import {connect} from 'react-redux';
import lazy from 'lazy.js';
import {gatherData, getDatesBetween} from '../../../shared/utils/utils';
import {_getUnreadReports, isAuthorized, isEntitled} from '../../../shared/store';
import {_getDefaultContainerID} from '../../reducers/defaultContainerID';
import {_openModal} from '../../../shared/reducers/modals';
import {Button, DatePicker, DescriptiveText, LoadingSpinner, RadioSet, SectionTitle, Steps, Table, Tabs} from '../../../shared';

const log = debug('circleblack:report:totaledHoldingsReport');

/**
 * Totaled Holdings Report component.
 *
 * @name TotaledHoldingsReport
 * @requires module:shared/components/Button
 * @requires module:shared/components/Table
 * @requires module:shared/components/Tabs
 * @requires module:shared/components/SectionTitle
 *
 */
class TotaledHoldingsReport extends Component {
  constructor(props) {
    super(props);
    this.report = props.report;
    this.cancelAction = props.cancelAction;
    this.containerID = 0;
    this.counts = {};
    this.validDates = [];
    this.tabSelections = {};
    this.state = {
      params: this.setupParams(this.report),
      preselectedRows: [],
      loading: true,
      isSavingDefaultSettings: false,
      step: 0,
      initialContainerID: this.containerID,
      initialContextCounts: {},
      containerStart: null,
      asOfDate: {},
      yesterday: moment().subtract(1, 'd').format('YYYY-MM-DD'),
      errorMessage: '',
      successMessage: '',
    };
    log('constructor state', this.state);
  }

  getCounts = () => axiosInstance.get('/containers/' + this.containerID + '/counts')
    .then(res => {
      this.counts = res.data;
    });

  // checkDate = () => {
  //   let mo;
  //   if (this.state.asOfDate.value !== this.state.params.asOfDate.value) {
  //     this.setState({asOfDate: {...this.state.asOfDate, value: this.state.params.asOfDate.value}});
  //   }
  //   const asOfDate = {...this.state.params.asOfDate};
  //   mo = moment(asOfDate.value);
  //   this.setState({asOfDate: {...asOfDate, valid: mo.isValid() && this.validDates.includes(mo.format('YYYY-MM-DD'))}});
  // };

  setupParams = report => {
    log('setupParams');
    const params = lazy(report.parameters).indexBy('name').toObject();
    for (let s = 0; s < report.settings.length; s++) {
      const param = lazy(params).findWhere({id: report.settings[s].parameterID});
      if ((report.settings[s].for.level > param.level) || (!param.level && !param.enforce)) {
        param.setting = report.settings[s].value;
        param.level = report.settings[s].for.level;
        param.enforce = report.settings[s].enforce;
      }
    }
    for (let p in params) {
      if (params.hasOwnProperty(p)) {
        const param = params[p];
        if (param.type === 'number' && !param.array) {
          param.value = (param.setting !== undefined ? parseFloat(param.setting) : param.default);
        } else {
          param.value = (param.setting !== undefined ? param.setting : param.default);
        }
      }
    }
    return params;
  };

  gatherPreSelectedData = () => {
    const {params} = this.state;
    const isClients = params.cat.setting.toUpperCase() === 'CLIENTS';
    let url = `/containers/${this.containerID}/${params.cat.setting}`;
    let queryParams = {params: {id: params.catIDs.value}};
    if (isClients) {
      url = `containers/${this.containerID}/${params.cat.setting}/actual`;
      queryParams = {params: {clientID: params.catIDs.value}};
    }
    return axiosInstance.get(
      url,
      queryParams
    )
      .then(result => {
        this.setState({preselectedRows: result.data});
      });
  };

  setContexts = (counts) => {
    let contextList = [];
    if (isAuthorized(['FIRMMGR'])) {
      if (counts.branches) {contextList.push('BRANCHES');}
    }
    if (isAuthorized(['BRANCHMGR', 'FIRMMGR'])) {
      if (counts.advisors) {contextList.push('ADVISORS');}
    }

    if (counts.households) {
      contextList.push('HOUSEHOLDS');
    }
    if (counts.clients) {
      contextList.push('CLIENTS');
    }
    if (counts.accounts) {
      contextList.push('ACCOUNTS');
    }
    return contextList;
  };

  componentDidMount() {
    const {params} = this.state;
    this.props.getDefaultContainerID()
      .then(() => {
        this.containerID = this.props.defaultContainerID;
        this.setState({initialContainerID: this.containerID});
        this.setDataManagers();
        return Promise.join(
          axiosInstance.get(`/containers/${this.containerID}`),
          // params.cat.setting && params.catIDs.setting.length ? this.gatherPreSelectedData() : Promise.resolve(), // TODO How to 'jump' into Totaled Holdings Report to a specific household/user selected.
          this.getCounts(),
          container => {
            const contexts = this.setContexts(this.counts);

            const restrictions = {
              max: this.state.yesterday,
              min: container.data.start,
            };

            this.setState({
              contexts,
              initialContextCounts: this.counts,
              initialContainerID: this.containerID,
              containerStart: container.data.start,
              asOfDate: {
                minDate: restrictions.min,
                maxDate: restrictions.max,
                valid: false,
                value: restrictions.max,
              },
              params: {
                ...params,
                cat: {
                  ...params.cat,
                  value: (contexts.includes(params.cat.value) && params.cat.value) || contexts[0],
                },
                asOfDate: {...params.asOfDate, value: restrictions.max},
              },
              loading: false,
            });
          }
        );
      });
  }

  updateParams = (name, value) => {
    const params = {...this.state.params};
    params[name].value = value;
    this.setState({params: params});
  };

  canGenerate = (params) => {
    if (!params || this.state.isSavingDefaultSettings) {
      return false;
    }
    return params.cat.value && (params.catIDs.value && params.catIDs.value.length) && this.state.asOfDate.valid;
  };

  generateReport = () => {
    if (isAuthorized(['CPLO'])) {
      return;
    }
    const {params} = this.state;
    log('Category', params.cat.value);
    log('Category IDs', params.catIDs.value);
    return axiosInstance.post(
      '/reports/' + this.report.name,
      {
        parameters: Object.values(this.state.params)
          .map(param => ({id: param.id, value: param.value})),
      }
    )
      .then(res => {
        let message = 'Your report is being generated. '
          + 'Once it is ready you will be notified and can view it in the Reports list or in your Document Vault.';

        if (res.data.messages && res.data.messages.length && res.data.messages[0].type === 'UNMAPPED_POS') {
          message += '\n\n' + res.data.messages[0].msg;
        }
        this.setState({
          ...this.state,
          successMessage: message,
          step: 'END',
        });

        this.props.getUnreadReports(
          this.props.unreadReportsTimeoutID, this.props.unreadReports, this.props.reportPageVisitTimestamp, 30000
        );
      })
      .catch(err => this.setState({
        errorMessage: (err.response && err.response.data && err.response.data.error && err.response.data.error.msg) || err.message,
      }));
  };

  saveSettings = () => {
    this.setState({
      ...this.state,
      isSavingDefaultSettings: true,
    });
    return Promise.each(
      Object.values(this.state.params)
        .filter(param => param.canSave)
        .filter(param => ![
          'startDate',
          'asOfDate',
        ].includes(param.name))
        .map(param => ({id: param.id, value: param.value}))
        .filter(param => param.value != null),
      param => axiosInstance.put('/reports/' + this.report.name, {
        parameter: param.id,
        value: param.value,
        for: {
          level: 5,
          id: this.props.session.profile.id,
        },
        report: this.report.id,
      })
    )
      .then(() => {
        this.setState({
          ...this.state,
          isSavingDefaultSettings: false,
        });
        return this.props.openModal({
          type: 'MessageModal',
          title: 'Saved Settings',
          body: 'Settings saved successfully.',
        });
      })
      .catch(() => {
        this.setState({
          ...this.state,
          isSavingDefaultSettings: false,
        });
        return this.props.openModal({
          type: 'MessageModal',
          title: 'Problem Saving Settings',
          body: 'There was a problem saving settings. Please try again.'
            + ' If the issue persists please contact Client Services to resolve the issue.',
        });
      });
  };

  updateSelectedRows = rows => {
    if (rows === 'ALL') {
      const profile = this.props.session.profile;
      let catIDs;
      let cat;
      switch (profile.role) {
        case 'FIRMMGR':
        case 'CPLO':
          catIDs = [profile.firm.id];
          cat = 'FIRMS';
          break;
        case 'BRANCHMGR':
          catIDs = [profile.id];
          cat = 'BRANCHES';
          break;
        case 'ADVISOR':
        default:
          catIDs = [profile.id];
          cat = 'ADVISORS';
      }
      return this.setState({
        params: {
          ...this.state.params,
          catIDs: {
            ...this.state.params.catIDs,
            value: catIDs,
          },
          cat: {
            ...this.state.params.cat,
            value: cat,
          },
        },
      });
    }
    return this.setState({
      params: {
        ...this.state.params,
        catIDs: {...this.state.params.catIDs, value: rows.map(row => row.id)},
      },
    });
  };

  setDataManagers = () => {
    const {initialContainerID} = this.state;

    this.branchDataManager = {
      gatherData: gatherData(`/containers/${initialContainerID}/branches`),
      setSelectedRows: this.updateSelectedRows,
    };

    this.advisorsDataManager = {
      gatherData: gatherData(`/containers/${initialContainerID}/advisors`),
      setSelectedRows: this.updateSelectedRows,
    };

    this.householdDataManager = {
      gatherData: gatherData(`/containers/${initialContainerID}/clients`),
      setSelectedRows: this.updateSelectedRows,
    };

    this.accountDataManager = {
      gatherData: gatherData(`/containers/${initialContainerID}/accounts`),
      setSelectedRows: this.updateSelectedRows,
    };

    this.clientDataManager = {
      gatherData: gatherData(`/containers/${initialContainerID}/clients/actual`),
      setSelectedRows: this.updateSelectedRows,
    };
  };

  incrementStep = () => this.setState({errorMessage: '', step: this.state.step + 1, loading: false});

  getValidDates = () => {
    const {cat, catIDs} = this.state.params;
    const gatheredDates = [];
    return Promise.map(
      catIDs.value,
      catID => axiosInstance.get(`/${cat.value}/${catID}/dates`)
        .then(result => {
          gatheredDates.push(...result.data);
        }), {concurrency: 3}
    )
      .then(() => {
        this.validDates = lazy(gatheredDates)
          .flatten()
          .uniq()
          .sort()
          .toArray();
        if (!this.validDates.length) {
          return this.props.openModal({
            type: 'MessageModal',
            title: 'No Valid Data',
            body: 'The selected context has no valid data and a report can not be generated.',
          });
        }
        this.setState({
          asOfDate: {
            minDate: this.validDates[0],
            maxDate: this.validDates[this.validDates.length - 1],
            valid: true,
            value: this.validDates[this.validDates.length - 1],
          },
          params: {
            ...this.state.params,
            asOfDate: {...this.state.params.asOfDate, value: this.validDates[this.validDates.length - 1]},
          },
        });
        this.incrementStep();
      });
    // TODO NO error handling per advisor v2, do we want to change this?
  };

  branch = {
    select: {
      type: 'all',
      max: 150,
      preselectedRows: [],
    },
  };

  advisor = {
    select: {
      type: 'all',
      max: 150,
      preselectedRows: [],
    },
  };

  household = {
    select: {
      type: 'all',
      max: 150,
      preselectedRows: [],
    },
  };

  client = {
    select: {
      type: 'all',
      max: 150,
      preselectedRows: [],
    },
  };

  account = {
    select: {
      type: 'all',
      max: 150,
      preselectedRows: [],
    },
  };

  setTabs = (contextCounts, tabs) => {
    let count = 0;
    const {setting} = this.state.params.cat;
    const {preselectedRows} = this.state;
    this.branch.select.preselectedRows = setting && setting.toUpperCase() === 'BRANCHES' ? preselectedRows : [];
    if (contextCounts.branches && isAuthorized(['FIRMMGR', 'CPLO'])) {
      this.tabSelections.branches = count++;
      tabs.push({
        title: 'Branches',
        value: 'BRANCHES',
        content: <Table dataManager={this.branchDataManager}
                        columns={[
                          {
                            title: 'Branch',
                            dataPoint: 'name',
                            type: 'text',
                            minWidth: 50,
                            isSortable: true,
                          },
                          {
                            title: 'Advisors',
                            dataPoint: 'counts.advisors.total',
                            type: 'number',
                            minWidth: 50,
                            isSortable: false,
                          },
                          {
                            title: 'Households',
                            dataPoint: 'counts.clients.total',
                            type: 'number',
                            minWidth: 50,
                            isSortable: false,
                          },
                          {
                            title: 'Accounts',
                            dataPoint: 'counts.accounts.total',
                            type: 'number',
                            minWidth: 50,
                            isSortable: false,
                          },
                          {
                            title: 'Total Assets',
                            dataPoint: 'amount',
                            type: 'currency',
                            minWidth: 50,
                            isSortable: true,
                          },
                        ]}
                        defaultSort={{
                          column: 'name',
                          desc: false,
                        }}
                        searches={[
                          {
                            title: 'Branch',
                            name: 'name',
                            isFuzzy: true,
                          },
                        ]}
                        select={this.branch.select}
                        rowIdentifier={'id'}
                        height={'350px'}/>,
      });
    }

    if (contextCounts.advisors && isAuthorized(['BRANCHMGR', 'FIRMMGR', 'CPLO'])) {
      this.tabSelections.advisors = count++;
      this.advisor.select.preselectedRows = setting && setting.toUpperCase() === 'ADVISORS' ? preselectedRows : [];
      tabs.push({
        title: 'Advisors',
        value: 'ADVISORS',
        content: <Table dataManager={this.advisorsDataManager}
                        columns={[
                          {
                            title: 'Advisor',
                            dataPoint: 'displayName',
                            type: 'text',
                            minWidth: 50,
                            isSortable: true,
                          },
                          {
                            title: 'Households',
                            dataPoint: 'counts.clients.total',
                            type: 'number',
                            minWidth: 50,
                            isSortable: false,
                          },
                          {
                            title: 'Accounts',
                            dataPoint: 'counts.accounts.total',
                            type: 'number',
                            minWidth: 50,
                            isSortable: false,
                          },
                          {
                            title: 'Total Assets',
                            dataPoint: 'amount',
                            type: 'currency',
                            minWidth: 50,
                            isSortable: true,
                          },
                        ]}
                        defaultSort={{
                          column: 'displayName',
                          desc: false,
                        }}
                        searches={[
                          {
                            title: 'Advisor',
                            name: 'displayName',
                            isFuzzy: true,
                          },
                        ]}
                        select={this.advisor.select}
                        rowIdentifier={'id'}
                        height={'350px'}/>,
      });
    }

    if (contextCounts.households) {
      this.tabSelections.households = count++;
      this.household.select.preselectedRows = setting && setting.toUpperCase() === 'HOUSEHOLDS' ? preselectedRows : [];
      tabs.push({
        title: 'Households',
        value: 'HOUSEHOLDS',
        content: <Table dataManager={this.householdDataManager}
                        columns={[
                          {
                            title: 'Household',
                            dataPoint: 'displayName',
                            type: 'text',
                            minWidth: 50,
                            isSortable: true,
                          },
                          {
                            title: 'Clients',
                            dataPoint: 'clientCounts',
                            type: 'number',
                            minWidth: 50,
                            isSortable: true,
                          },
                          {
                            title: 'Accounts',
                            dataPoint: 'totalAccounts',
                            type: 'number',
                            minWidth: 50,
                            isSortable: true,
                          },
                          {
                            title: 'Total Assets',
                            dataPoint: 'amount',
                            type: 'currency',
                            minWidth: 50,
                            isSortable: true,
                          },
                        ]}
                        defaultSort={{
                          column: 'displayName',
                          desc: false,
                        }}
                        searches={[
                          {
                            title: 'Household',
                            name: 'displayName',
                            isFuzzy: true,
                          },
                        ]}
                        select={this.household.select}
                        rowIdentifier={'id'}
                        height={'350px'}/>,
      });
    }

    if (contextCounts.clients) {
      this.tabSelections.clients = count++;
      this.client.select.preselectedRows = setting && setting.toUpperCase() === 'CLIENTS' ? preselectedRows : [];
      tabs.push({
        title: 'Clients',
        value: 'CLIENTS',
        content: <Table dataManager={this.clientDataManager}
                        columns={[
                          {
                            title: 'Client',
                            dataPoint: 'displayName',
                            type: 'text',
                            minWidth: 50,
                            isSortable: true,
                          },
                          {
                            title: 'Tax ID',
                            dataPoint: 'taxID',
                            type: 'text',
                            minWidth: 50,
                            isSortable: true,
                          },
                          {
                            title: 'Accounts',
                            dataPoint: 'totalAccounts',
                            type: 'number',
                            minWidth: 50,
                            isSortable: true,
                          },
                          {
                            title: 'Total Assets',
                            dataPoint: 'balance',
                            type: 'currency',
                            minWidth: 50,
                            isSortable: true,
                          },
                        ]}
                        defaultSort={{
                          column: 'displayName',
                          desc: false,
                        }}
                        searches={[
                          {
                            title: 'Client',
                            name: 'displayName',
                            isFuzzy: true,
                          },
                          {
                            title: 'Tax ID',
                            name: 'taxID',
                            isFuzzy: true,
                          },
                        ]}
                        select={this.client.select}
                        rowIdentifier={'id'}
                        height={'350px'}/>,

      });
    }

    if (contextCounts.accounts) {
      this.tabSelections.accounts = count++;
      this.account.select.preselectedRows = setting && setting.toUpperCase() === 'ACCOUNTS' ? preselectedRows : [];
      tabs.push({
        title: 'Accounts',
        value: 'ACCOUNTS',
        content: <Table dataManager={this.accountDataManager}
                        columns={[
                          {
                            title: 'Account',
                            dataPoint: 'searchName',
                            type: 'text',
                            minWidth: 50,
                            isSortable: true,
                          },
                          {
                            title: 'Client',
                            dataPoint: 'clientFullName',
                            type: 'text',
                            minWidth: 50,
                            isSortable: false,
                          },
                          {
                            title: 'Type',
                            dataPoint: 'typeName',
                            type: 'number',
                            minWidth: 50,
                            isSortable: true,
                          },
                          {
                            title: 'Assets',
                            dataPoint: 'balance',
                            type: 'currency',
                            minWidth: 50,
                            isSortable: true,
                          },
                        ]}
                        defaultSort={{
                          column: 'displayName',
                          desc: false,
                        }}
                        searches={[
                          {
                            title: 'Account',
                            name: 'searchName',
                            isFuzzy: true,
                          },
                          {
                            title: 'Client',
                            name: 'clientFullName',
                            isFuzzy: true,
                          },
                          {
                            title: 'Type',
                            name: 'typeName',
                            isFuzzy: true,
                          },
                        ]}
                        select={this.account.select}
                        rowIdentifier={'id'}
                        height={'350px'}/>,
      });
    }
  };

  render() {
    log('rendering', this.props);
    const isEntitledBroadAssetClass = isEntitled([{group: 'exposure', feature: 'broad', access: 'r'}]);
    const groupingOptions = [
      {label: 'No Grouping', value: 'NONE'},
      {label: isEntitledBroadAssetClass ? 'By Asset Class Detailed' : 'By Asset Class', value: 'ASSET_CLASS_DETAILED'},
    ];

    if (isEntitledBroadAssetClass) {
      groupingOptions.push({label: 'By Asset Class', value: 'ASSET_CLASS'});
    }

    if (this.state.loading) {
      return <LoadingSpinner/>;
    }
    const {
      params,
      step,
      initialContextCounts,
      asOfDate,
    } = this.state;
    log('params', params);

    const detailedTabs = [];

    this.setTabs(initialContextCounts, detailedTabs);

    const contextSelection = <Tabs
      tabs={detailedTabs}
      displayTabTitles={true}
      tabWidth='150px'
      minTabWidth='70px'
      defaultSelection={this.tabSelections[params.cat.value.toLowerCase()] || 0}
      onChange={tabIndex => this.setState({params: {...params, cat: {...params.cat, value: detailedTabs[tabIndex].value}}})}
    />;

    const parameterSelection = () => <div>
      <Button importance={'secondary'}
              size={'md'}
              label={'Save as Default Settings'}
              disabled={false}
              action={() => this.saveSettings(params)}/>
      <DatePicker title={'As Of Date'}
                  maxDate={asOfDate.maxDate}
                  minDate={asOfDate.minDate}
                  dataPoint={{name: 'asOfDate', value: params.asOfDate.value}}
                  disabledDates={getDatesBetween(asOfDate.minDate, asOfDate.maxDate).filter(d => !this.validDates.includes(d))}
                  onChange={(name, value) => {
                    params.asOfDate.value = value;
                    this.setState({params});
                  }}/>
      <SectionTitle title={'Grouping Options'}/>
      <RadioSet dataPoint={{name: 'grouping', value: this.state.params.grouping.value}}
                readOnly={false}
                options={groupingOptions}
                orientation='row' // TODO KST-1679 orientation seems to not work!
                onChange={this.updateParams}
                layout={{grow: 1}}
      />
      <SectionTitle title={'Format'}/>
      <RadioSet dataPoint={{name: 'format', value: this.state.params.format.value}}
                readOnly={false}
                options={[{label: 'PDF', value: 'pdf'}, {label: 'Excel', value: 'xlsx'}]}
                orientation='row' // TODO KST-1679 orientation seems to not work!
                onChange={this.updateParams}
                layout={{grow: 1}}
      />
    </div>;

    // TODO Steps needs a cancel action for Totaled Holdings Report, check all reports
    if (!isAuthorized(['CPLO'])) {
      return <Steps
        height='100%'
        completedStateContent={<DescriptiveText>{this.state.successMessage}</DescriptiveText> || ''}
        steps={[
          {
            title: `Generate ${this.report.name} - Select Context`,
            content: contextSelection,
            next: {
              action: () => this.getValidDates(),
              disabled: !(params.cat && params.cat.value && params.catIDs.value && params.catIDs.value.length),
            },
          },
          {
            title: `Generate ${this.report.name} - Select Parameters`,
            content: parameterSelection,
            next: {
              label: 'Generate',
              action: this.generateReport,
              disabled: !this.canGenerate(this.state.params),
            },
          },
        ]}
        stepIndex={step}
        canStepBack={false}
        cancelAction={this.cancelAction}
        errorMessage={this.state.errorMessage}
      />;
    }
    return '';
  }
}

const mapStateToProps = state => ({
  session: state.session,
  defaultContainerID: state.defaultContainerID,
  unreadReportsTimeoutID: state.badges.unreadReportsTimeoutID,
  unreadReports: state.badges.unreadReports,
  reportPageVisitTimestamp: state.badges.reportPageVisitTimestamp,
});

const mapDispatchToProps = (dispatch) => ({
  openModal: modalToOpen => dispatch(_openModal(modalToOpen)),
  getDefaultContainerID: () => dispatch(_getDefaultContainerID()),
  getUnreadReports: (timeoutID, unread, timestamp, interval) => dispatch(_getUnreadReports(timeoutID, unread, timestamp, interval)),
});

TotaledHoldingsReport.propTypes = {
  /**
   * The Report to be generated
   */
  report: PropTypes.shape({}).isRequired,
  /**
   * Is from Models Reducer
   */
  openModal: PropTypes.func.isRequired,
  /**
   * Is from Default Container Reducer
   */
  getDefaultContainerID: PropTypes.func.isRequired,
  /**
   * Is from Default Container Reducer
   */
  defaultContainerID: PropTypes.number.isRequired,
  /**
   /**
   * Is Session Reducer
   */
  session: PropTypes.shape({profile: PropTypes.object}).isRequired,
  /**
   * To reset the Report generate tile to default state
   */
  cancelAction: PropTypes.func.isRequired,
  /**
   * Are from badges reducer
   */
  getUnreadReports: PropTypes.func,
  unreadReportsTimeoutID: PropTypes.object,
  unreadReports: PropTypes.object,
  reportPageVisitTimestamp: PropTypes.object,
};

export default connect(mapStateToProps, mapDispatchToProps)(TotaledHoldingsReport);
